import { ulid, type Asignacion, type Cliente, type ModoEfectivo, type Posicion } from '@haus/core';

import {
    guardarIncidencia,
    guardarLimpieza,
    leerLimpieza,
    type EstanciaLocal,
    type LimpiezaLocal,
} from './db.js';

/**
 * Empezar, avanzar y cerrar una limpieza, sin depender de la red.
 *
 * TODO lo de aquí escribe primero en IndexedDB y avisa al servidor después,
 * nunca al revés. La empleada toca "Empezar" en el portal de un edificio de
 * paredes gruesas: si la pantalla siguiente esperara una respuesta del
 * servidor, la app se quedaría colgada justo en el momento en que tiene que
 * funcionar.
 *
 * Los identificadores los genera el móvil (ULID), así que cuando la limpieza
 * llegue al servidor —dentro de un minuto o de tres horas— será la misma
 * limpieza y no una copia.
 */

export function estanciasDeAsignacion(a: Asignacion): EstanciaLocal[] {
    return a.apartamento.estancias.map((e) => ({
        id: ulid(),
        room_id: e.id,
        nombre_es: e.nombre.es,
        nombre_en: e.nombre.en ?? null,
        nombre_de: e.nombre.de ?? null,
        guia_es: e.guia.es ?? null,
        guia_en: e.guia.en ?? null,
        guia_de: e.guia.de ?? null,
        orden: e.orden,
        min_fotos: e.min_fotos,
        min_video_s: e.min_video_s,
        max_video_s: e.max_video_s,
        puntos: e.puntos.map((p) => ({
            id: p.id,
            slug: p.slug,
            etiqueta_es: p.etiqueta.es,
            etiqueta_en: p.etiqueta.en ?? null,
            etiqueta_de: p.etiqueta.de ?? null,
        })),
        estado: 'pendiente',
        motivo_no_accesible: null,
        video_ms: null,
        fotos: 0,
    }));
}

/**
 * Crea la limpieza en el móvil.
 *
 * El modo de captura se **congela** aquí: si oficina cambia el modo del
 * apartamento a media mañana, la limpieza en curso no se entera. Lo que se
 * grabó, se grabó con las reglas de entonces, y eso es lo que hace que la
 * prueba sea coherente consigo misma.
 */
export async function empezarLimpieza(
    a: Asignacion,
    modo: ModoEfectivo,
    gps: Posicion | null
): Promise<LimpiezaLocal> {
    const l: LimpiezaLocal = {
        id: ulid(),
        assignment_id: a.assignment_id,
        property_id: a.apartamento.id,
        apartamento: a.apartamento.nombre,
        modo,
        started_at: new Date().toISOString(),
        ended_at: null,
        gps,
        notas: null,
        sincronizada: false,
        finalizada: false,
        estancias: estanciasDeAsignacion(a),
    };

    await guardarLimpieza(l);

    return l;
}

/**
 * Intenta registrar la limpieza en el servidor.
 *
 * Si falla, no pasa nada: queda `sincronizada: false` y la cola lo hará antes
 * de subir el primer clip. Se llama de todas formas porque, cuando hay red, es
 * mejor que el panel de oficina vea "en curso" desde el minuto uno: así
 * "2 sin grabar" a las 20:00 significa lo que dice.
 */
export async function sincronizarLimpieza(cliente: Cliente, l: LimpiezaLocal): Promise<boolean> {
    if (l.sincronizada) {
        return true;
    }

    try {
        await cliente.crearLimpieza({
            id: l.id,
            property_id: l.property_id,
            assignment_id: l.assignment_id,
            started_at: l.started_at,
            modo_captura: l.modo,
            gps: l.gps,
            estancias: l.estancias.map((e) => ({ id: e.id, room_id: e.room_id })),
        });
        await guardarLimpieza({ ...l, sincronizada: true });

        return true;
    } catch {
        return false;
    }
}

export async function marcarEstancia(
    limpiezaId: string,
    estanciaId: string,
    cambios: Partial<EstanciaLocal>
): Promise<LimpiezaLocal | null> {
    const l = await leerLimpieza(limpiezaId);
    if (l === null) {
        return null;
    }

    const nueva: LimpiezaLocal = {
        ...l,
        estancias: l.estancias.map((e) => (e.id === estanciaId ? { ...e, ...cambios } : e)),
    };
    await guardarLimpieza(nueva);

    return nueva;
}

export function siguienteEstancia(l: LimpiezaLocal): EstanciaLocal | null {
    return [...l.estancias].sort((a, b) => a.orden - b.orden).find((e) => e.estado === 'pendiente') ?? null;
}

export function estanciasPendientes(l: LimpiezaLocal): number {
    return l.estancias.filter((e) => e.estado === 'pendiente').length;
}

/**
 * Añade una incidencia. Va a la cola como todo lo demás.
 */
export async function anotarIncidencia(datos: {
    cleaning_id: string;
    cleaning_room_id: string | null;
    texto: string;
    gravedad: 'baja' | 'media' | 'alta';
}): Promise<string> {
    const id = ulid();
    await guardarIncidencia({
        id,
        cleaning_id: datos.cleaning_id,
        cleaning_room_id: datos.cleaning_room_id,
        texto: datos.texto,
        gravedad: datos.gravedad,
        sincronizada: false,
        creado_at: Date.now(),
    });

    return id;
}
